package com.kinoshazam.screens.main.compact

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.components.cells.cellItem.CellItem
import com.kinoshazam.uiKit.components.cells.cellItem.CellItemState
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.videoItem.CompactVideoItem
import com.kinoshazam.uiKit.components.videoItem.compactBannerSkeletons
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.arrow_right
import kinoshazam.resources.generated.resources.cartoons
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.popular_cartoons
import kinoshazam.resources.generated.resources.popular_tv_cartoons
import kinoshazam.resources.generated.resources.tv_show_cartoon
import kinoshazam.resources.generated.resources.watch_all
import kinoshazam.resources.generated.resources.which_watch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.allVideos.AllVideosState.ScreenType.Cartoons
import presentation.screens.main.MainMVIAction
import presentation.screens.main.MainState

@Composable
fun CartoonContent(
    paddingValues: PaddingValues,
    state: MainState,
    onAction: (MainMVIAction) -> Unit,
) {
    val scrollState = rememberLazyGridState()
    val fetchNextPage: Boolean by remember {
        derivedStateOf {
            val currentVideoCount = scrollState.layoutInfo.totalItemsCount - 1
            val lastVisibleIndex =
                scrollState.layoutInfo.visibleItemsInfo.lastOrNull()?.index
                    ?: return@derivedStateOf false
            return@derivedStateOf lastVisibleIndex >= currentVideoCount - 5
        }
    }

    LaunchedEffect(key1 = fetchNextPage) {
        if (fetchNextPage) {
            onAction(MainMVIAction.FetchNextPage)
        }
    }

    LazyColumn(
        modifier = Modifier.padding(paddingValues)
    ) {

        item {
            CellItem(
                state = CellItemState(
                    title = TextLabelState(
                        text = buildAnnotatedString {
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                    fontWeight = BoldMedium.fontWeight
                                )
                            ) {
                                append("${stringResource(Res.string.cartoons)},")
                            }
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                )
                            ) {
                                append(" ${stringResource(Res.string.which_watch)}")
                            }
                        }
                    )
                )
            )

            val cartoonList = state.cartoonsVideos
            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                contentPadding = PaddingValues(start = 16.dp, end = 16.dp)
            ) {
                if (cartoonList == null) {
                    compactBannerSkeletons()
                } else itemsIndexed(cartoonList) { index, video ->
                    CompactVideoItem(
                        state = VideoState(
                            id = video.id ?: -1,
                            title = video.title.orEmpty(),
                            image = video.imageUrl,
                            placeHolder = painterResource(Res.drawable.movie),
                            placeHolderColor = UiKitTheme.colors.color_5,
                            onAction = { id ->
                                if (video.isTvShow) {
                                    onAction(MainMVIAction.OnTvShowClicked(id))
                                } else {
                                    onAction(MainMVIAction.OnDetailsClicked(id))
                                }
                            }
                        ),
                    )
                }
                item {
                    val cartoons = stringResource(Res.string.popular_cartoons)
                    CompactVideoItem(
                        state = VideoState(
                            id = 1,
                            title = stringResource(Res.string.watch_all),
                            placeHolder = painterResource(Res.drawable.arrow_right),
                            placeHolderColor = UiKitTheme.colors.color_2,
                            onAction = {
                                onAction(
                                    MainMVIAction.OnAllVideoClicked(
                                        screenType = Cartoons,
                                        title = cartoons,
                                        isSerial = false,
                                    )
                                )
                            }
                        ),
                    )
                }
            }
        }

        item {
            CellItem(
                state = CellItemState(
                    title = TextLabelState(
                        text = buildAnnotatedString {
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                    fontWeight = BoldMedium.fontWeight
                                )
                            ) {
                                append("${stringResource(Res.string.tv_show_cartoon)},")
                            }
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                )
                            ) {
                                append(" ${stringResource(Res.string.which_watch)}")
                            }
                        }
                    )
                )
            )

            val cartoonTvList = state.cartoonsTvVideos
            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                contentPadding = PaddingValues(start = 16.dp, end = 16.dp)
            ) {
                if (cartoonTvList == null) {
                    compactBannerSkeletons()
                } else itemsIndexed(cartoonTvList) { index, video ->
                    CompactVideoItem(
                        state = VideoState(
                            id = video.id ?: -1,
                            title = video.title.orEmpty(),
                            image = video.imageUrl,
                            placeHolder = painterResource(Res.drawable.movie),
                            placeHolderColor = UiKitTheme.colors.color_5,
                            onAction = { id ->
                                if (video.isTvShow) {
                                    onAction(MainMVIAction.OnTvShowClicked(id))
                                } else {
                                    onAction(MainMVIAction.OnDetailsClicked(id))
                                }
                            }
                        ),
                    )
                }
                item {
                    val tvCartoons = stringResource(Res.string.popular_tv_cartoons)
                    CompactVideoItem(
                        state = VideoState(
                            id = 1,
                            title = stringResource(Res.string.watch_all),
                            placeHolder = painterResource(Res.drawable.arrow_right),
                            placeHolderColor = UiKitTheme.colors.color_2,
                            onAction = {
                                onAction(
                                    MainMVIAction.OnAllVideoClicked(
                                        screenType = Cartoons,
                                        title = tvCartoons,
                                        isSerial = true,
                                    )
                                )
                            }
                        ),
                    )
                }
            }
        }

    }
}